import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MaintenancePage from './pages/MaintenancePage';
import Header from './components/layout/Header';
import Sidebar from './components/layout/Sidebar';
import TopBar from './components/layout/TopBar';
import CommandPalette from './components/CommandPalette';
import Tutorial from './components/Tutorial';
import AppRoutes from './components/AppRoutes';
import PublicRoutes from './components/PublicRoutes';
import AlertModal from './components/AlertModal';
import ConfirmModal from './components/ConfirmModal';
import { clearExpiredCache } from './hooks/useImageCache';
import { profileAPI, authAPI, subscriptionAPI, dateGenAPI, AUTH_EXPIRED_EVENT, clearAuthStorage } from './api';
import { useWebSocket } from './hooks/useWebSocket';
import { useShellCounters } from './hooks/useShellCounters';
import { WishesProvider } from './context/WishesContext';
import { StylesProvider } from './context/StylesContext';
import { pageIdForPath, pathForPage } from './constants/navigation';
import './App.css';

function App() {
    const navigate = useNavigate();
    const location = useLocation();
    // The page id the rest of the app still speaks (sidebar active state,
    // tutorial steps), derived from the URL rather than kept in state.
    const currentPage = pageIdForPath(location.pathname);

    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [sessionExpired, setSessionExpired] = useState(false);
    const [donationAlert, setDonationAlert] = useState(null);
    const [username, setUsername] = useState('');
    const [isAdmin, setIsAdmin] = useState(false);
    const [isModerator, setIsModerator] = useState(false);
    const [isContentCreator, setIsContentCreator] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
        try { return localStorage.getItem('al_sidebar_collapsed') === 'true'; } catch (e) { return false; }
    });
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

    // Stable handler so Sidebar isn't handed a new function every render.
    const handleToggleSidebarCollapse = useCallback(() => {
        setSidebarCollapsed((c) => {
            const next = !c;
            try { localStorage.setItem('al_sidebar_collapsed', String(next)); } catch (e) { /* ignore */ }
            return next;
        });
    }, []);
    const [competitionPhaseAlert, setCompetitionPhaseAlert] = useState(null); // Competition phase change notification
    const [cardUpdate, setCardUpdate] = useState(null); // Card websocket update data
    const [dateUpdate, setDateUpdate] = useState(null); // Date websocket update data
    const [userProfile, setUserProfile] = useState(null);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [unclaimedBenefits, setUnclaimedBenefits] = useState(0);
    const [dateGenStatus, setDateGenStatus] = useState(null);
    const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
    const [maintenanceChecked, setMaintenanceChecked] = useState(false);
    const [isDevServer, setIsDevServer] = useState(false);
    const [isDivinationEnabled, setIsDivinationEnabled] = useState(false);
    const [isBoostersEnabled, setIsBoostersEnabled] = useState(false);

    // Sidebar badge counts, fetched and kept fresh in one place.
    const {
        expeditionsToCollect,
        competitionRewardsToClaim,
        tradeProposalsPending,
        refreshCountsIfStale,
        refreshExpeditions,
        refreshCompetitionRewards,
        refreshTradeProposals,
        addCompetitionReward,
        addTradeProposal,
    } = useShellCounters(isAuthenticated);

    // Single centralized WebSocket connection for the entire app
    useWebSocket({
        onCardUpdate: useCallback((data) => {
            // Pass card update data to CollectionPage
            setCardUpdate(data);
        }, []),
        onDateUpdate: useCallback((data) => {
            // Pass date update data to CollectionPage (dates are shown in card history)
            setDateUpdate(data);
        }, []),
        onCompetitionRewards: addCompetitionReward,
        onCompetitionPhaseChange: useCallback((data) => {
            // Show notification for competition phase changes (submission or voting)
            setCompetitionPhaseAlert({
                phase: data.phase,
                message: data.message,
                competition_title: data.competition_title,
                competition_id: data.competition_id
            });
        }, []),
        onTradeProposal: useCallback((data) => {
            if (data.action === 'created') {
                addTradeProposal();
            }
        }, [addTradeProposal]),
        onConnect: useCallback(() => {
            // The socket can reconnect often on a flaky link; only re-fetch if
            // the counts have had time to drift since the last refresh.
            if (isAuthenticated) refreshCountsIfStale();
        }, [isAuthenticated, refreshCountsIfStale]),
    });

    useEffect(() => {
        // Check system status (maintenance mode, environment, etc.) in a single call
        const checkSystemStatus = async () => {
            try {
                const response = await authAPI.getSystemStatus();
                setIsMaintenanceMode(response.data.maintenance_mode || false);
                setIsDevServer(response.data.is_dev || false);
                setIsDivinationEnabled(response.data.divination_enabled || false);
                setIsBoostersEnabled(response.data.boosters_enabled || false);
            } catch (error) {
                // If the endpoint fails, assume production and not in maintenance
                setIsMaintenanceMode(false);
                setIsDevServer(false);
                setIsDivinationEnabled(false);
                setIsBoostersEnabled(false);
            } finally {
                setMaintenanceChecked(true);
            }
        };

        checkSystemStatus();

        // Check if user is already logged in
        const token = localStorage.getItem('authToken');
        const storedUsername = localStorage.getItem('username');
        const storedIsStaff = localStorage.getItem('isStaff');
        const storedIsModerator = localStorage.getItem('isModerator');
        const storedIsContentCreator = localStorage.getItem('isContentCreator');
        if (token && storedUsername) {
            setIsAuthenticated(true);
            setUsername(storedUsername);
            // Check if user is admin from stored value
            setIsAdmin(storedIsStaff === 'true');
            setIsModerator(storedIsModerator === 'true');
            setIsContentCreator(storedIsContentCreator === 'true');
        }

        // Clear expired cache on app startup
        clearExpiredCache().catch(() => {
            // Silently fail - cache cleanup is non-critical
        });
    }, []);

    // Stripe sends the donor back with ?donation=success|cancelled; acknowledge
    // it once, then drop the query string so a refresh doesn't repeat the modal.
    useEffect(() => {
        const donationStatus = new URLSearchParams(location.search).get('donation');
        if (!donationStatus) return;

        if (donationStatus === 'success') {
            setDonationAlert({
                type: 'info',
                message: 'Your donation helps us keep the servers running and develop new features. We truly appreciate your support!'
            });
        } else if (donationStatus === 'cancelled') {
            setDonationAlert({
                type: 'cancelled',
                message: 'No worries! You can donate anytime from the Donate button in the header menu.'
            });
        }
        navigate(location.pathname, { replace: true });
    }, [location.search, location.pathname, navigate]);

    // Fetch user profile when authenticated
    const fetchUserProfile = useCallback(async () => {
        if (!isAuthenticated) return;
        try {
            const response = await profileAPI.getMyProfile();
            setUserProfile(response.data);
        } catch (error) {
            // Silently fail - profile fetch is non-critical
        }
    }, [isAuthenticated]);

    // Fetch subscription status when authenticated
    const fetchSubscriptionStatus = useCallback(async () => {
        if (!isAuthenticated) return;
        try {
            const response = await subscriptionAPI.getStatus();
            setIsSubscribed(response.data?.is_subscribed || false);
            // Count unclaimed weekly benefits
            const benefits = response.data?.weekly_benefits;
            if (benefits && response.data?.is_subscribed) {
                const unclaimed = (benefits.theme_cards?.available || 0)
                    + (benefits.video_wishes?.available || 0)
                    + (benefits.mutation_items?.available || 0)
                    + (benefits.slot?.available || 0);
                setUnclaimedBenefits(unclaimed);
            } else {
                setUnclaimedBenefits(0);
            }
        } catch (error) {
            // User may not have subscription - not an error
            setIsSubscribed(false);
            setUnclaimedBenefits(0);
        }
    }, [isAuthenticated]);

    // Fetch date generation status
    const fetchDateGenStatus = useCallback(async () => {
        if (!isAuthenticated) return;
        try {
            const response = await dateGenAPI.getStatus();
            setDateGenStatus(response.data);
        } catch {
            setDateGenStatus(null);
        }
    }, [isAuthenticated]);

    // The API client clears the stored session and fires this the moment the
    // server rejects our token, from wherever the call was made.
    useEffect(() => {
        const handleAuthExpired = () => {
            setIsAuthenticated(false);
            setUsername('');
            setIsAdmin(false);
            setIsModerator(false);
            setIsContentCreator(false);
            setIsSubscribed(false);
            setUserProfile(null);
            setSessionExpired(true);
        };
        window.addEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
        return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
    }, []);

    // Listen for date-gen-changed events from anywhere in the app
    useEffect(() => {
        const handler = () => fetchDateGenStatus();
        window.addEventListener('date-gen-changed', handler);
        return () => window.removeEventListener('date-gen-changed', handler);
    }, [fetchDateGenStatus]);

    useEffect(() => {
        if (isAuthenticated) {
            fetchUserProfile();
            fetchSubscriptionStatus();
            fetchDateGenStatus();
        }
    }, [isAuthenticated, fetchUserProfile, fetchSubscriptionStatus, fetchDateGenStatus]);

    // Opening the page a badge refers to is a good moment to re-check it.
    useEffect(() => {
        if (!isAuthenticated) return;
        if (currentPage === 'expeditions-hub') refreshExpeditions();
        if (currentPage === 'minigames') refreshCompetitionRewards();
        if (currentPage === 'trades') refreshTradeProposals();
    }, [currentPage, isAuthenticated, refreshExpeditions, refreshCompetitionRewards, refreshTradeProposals]);

    // Navigate by page id, so children keep the `onNavigate(id)` contract they
    // had before the app had real URLs.
    const navigateTo = useCallback((page) => {
        navigate(pathForPage(page));
    }, [navigate]);

    const handleLoginSuccess = (data) => {
        setIsAuthenticated(true);
        setSessionExpired(false);
        setUsername(data.username);
        // Check if user is admin from API response
        const isStaff = data.is_staff || false;
        const isMod = data.is_moderator || false;
        const isCreator = data.is_content_creator || false;
        setIsAdmin(isStaff);
        setIsModerator(isMod);
        setIsContentCreator(isCreator);
        localStorage.setItem('isStaff', isStaff.toString());
        localStorage.setItem('isModerator', isMod.toString());
        localStorage.setItem('isContentCreator', isCreator.toString());
    };

    const handleRegisterSuccess = (data) => {
        setIsAuthenticated(true);
        setSessionExpired(false);
        setUsername(data.username);
        // Check if user is admin from API response
        const isStaff = data.is_staff || false;
        const isMod = data.is_moderator || false;
        const isCreator = data.is_content_creator || false;
        setIsAdmin(isStaff);
        setIsModerator(isMod);
        setIsContentCreator(isCreator);
        localStorage.setItem('isStaff', isStaff.toString());
        localStorage.setItem('isModerator', isMod.toString());
        localStorage.setItem('isContentCreator', isCreator.toString());
    };

    const handleLogout = () => {
        clearAuthStorage();
        setIsAuthenticated(false);
        setSessionExpired(false);
        setUsername('');
        setIsAdmin(false);
        setIsModerator(false);
        setIsContentCreator(false);
        setIsSubscribed(false);
        navigate('/');
    };

    if (!maintenanceChecked) {
        // Show loading while checking maintenance mode
        return (
            <div className="App">
                <div className="container loading-container">
                    <p>Loading...</p>
                </div>
            </div>
        );
    }

    if (isMaintenanceMode && !isAdmin && !isModerator) {
        return <MaintenancePage onLoginSuccess={handleLoginSuccess} />;
    }

    if (!isAuthenticated) {
        return (
            <div className="App">
                <Header
                    isAuthenticated={false}
                />
                <div className="container">
                    <PublicRoutes
                        onLoginSuccess={handleLoginSuccess}
                        onRegisterSuccess={handleRegisterSuccess}
                        notice={sessionExpired ? 'Session expired. Please sign in again.' : null}
                    />
                </div>
            </div>
        );
    }

    return (
        <WishesProvider>
          <StylesProvider>
            <div className={`app-shell${isDevServer ? ' app-shell--with-dev-banner' : ''}`}>
                {isDevServer && (
                    <div className="dev-banner">
                        ⚠️ DEVELOPMENT SERVER ⚠️
                    </div>
                )}
                <Sidebar
                    currentPage={currentPage}
                    onNavigate={navigateTo}
                    isAdmin={isAdmin}
                    isModerator={isModerator}
                    isContentCreator={isContentCreator}
                    dateGenStatus={dateGenStatus}
                    expeditionsToCollect={expeditionsToCollect}
                    competitionRewardsToClaim={competitionRewardsToClaim}
                    tradeProposalsPending={tradeProposalsPending}
                    boostersEnabled={isBoostersEnabled}
                    collapsed={sidebarCollapsed}
                    onToggleCollapse={handleToggleSidebarCollapse}
                    mobileOpen={mobileSidebarOpen}
                    onCloseMobile={() => setMobileSidebarOpen(false)}
                />
                <CommandPalette
                    onNavigate={navigateTo}
                    isAdmin={isAdmin}
                    isModerator={isModerator}
                    isContentCreator={isContentCreator}
                />
                <div className="app-shell-body">
                    <TopBar
                        username={username}
                        onLogout={handleLogout}
                        onNavigate={navigateTo}
                        isSubscribed={isSubscribed}
                        unclaimedBenefits={unclaimedBenefits}
                        dateGenStatus={dateGenStatus}
                        onToggleMobileSidebar={() => setMobileSidebarOpen((o) => !o)}
                        onCloseMobile={() => setMobileSidebarOpen(false)}
                    />

                    <main className="app-main app-content" key={location.pathname}>
                        <AppRoutes
                            isAdmin={isAdmin}
                            isModerator={isModerator}
                            isContentCreator={isContentCreator}
                            boostersEnabled={isBoostersEnabled}
                            divinationEnabled={isDivinationEnabled}
                            cardUpdate={cardUpdate}
                            dateUpdate={dateUpdate}
                            userProfile={userProfile}
                            onTradeProposalsChange={refreshTradeProposals}
                            onExpeditionUpdate={refreshExpeditions}
                            onCompetitionRewardClaimed={refreshCompetitionRewards}
                            onSubscriptionChange={fetchSubscriptionStatus}
                            onDateGenChange={fetchDateGenStatus}
                        />
                    </main>

                    {/* Tutorial overlay */}
                    {userProfile && (
                        <Tutorial
                            currentPage={currentPage}
                            onNavigate={navigateTo}
                            userProfile={userProfile}
                            onUpdateProfile={fetchUserProfile}
                            divinationEnabled={isDivinationEnabled}
                            boostersEnabled={isBoostersEnabled}
                        />
                    )}

                    {/* Donation alert */}
                    {donationAlert && (
                        <AlertModal
                            type={donationAlert.type === 'info' ? 'success' : 'info'}
                            message={donationAlert.message}
                            onClose={() => setDonationAlert(null)}
                        />
                    )}

                    {/* Competition phase change alert — ConfirmModal rather than
                        AlertModal because it offers a "go there now" action. */}
                    {competitionPhaseAlert && (
                        <ConfirmModal
                            message={competitionPhaseAlert.message}
                            confirmText="Go to Competition"
                            confirmStyle="primary"
                            cancelText="Close"
                            onConfirm={() => {
                                setCompetitionPhaseAlert(null);
                                navigateTo('minigames');
                            }}
                            onCancel={() => setCompetitionPhaseAlert(null)}
                        />
                    )}
                </div>
            </div>
          </StylesProvider>
        </WishesProvider>
    );
}

export default App;
